import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import { Typography } from '@material-ui/core';
import { isEmpty, isArray } from 'lodash';
import classNames from 'classnames';
import { ADGMPanel, ADGMGridLayoutV2 } from 'aurora-components';
import { DocumentsV2, AddRepresentative } from 'aurora-section-components';
import { keyEmptyCheck } from 'aurora-utils/utils';
import useBuyerDetailsActions from '../helpers';

import { BUYER_TYPES, BUYER_DOCUMENTS_ATTRIBUTES } from '../constants';

import styles from './styles';

const BuyersReview = ({ classes, locale, application, buyers, documents, downloadDocumentHandler }) => {
  if (!isArray(buyers) || isEmpty(buyers)) return null;

  const {
    getBuyerType,
    transformDetails,
    getBuyerDocuments,
    getBuyerRepresentativeDocuments,
  } = useBuyerDetailsActions();

  const getBuyerGridLayoutDetails = useCallback(
    (buyer, buyerType) => {
      return [
        {
          label: 'Name',
          value: locale === 'en' ? buyer?.nameEn || buyer?.nameAr : buyer?.nameAr || buyer?.nameEn,
          isPersonName: true,
        },
        ...(buyerType === BUYER_TYPES?.UAE_RESIDENT
          ? [
              {
                label: 'emirates_ID',
                value: buyer?.eid,
              },
              {
                label: 'nationality',
                value:
                  locale === 'en'
                    ? buyer?.nationalityEn || buyer?.nationalityAr
                    : buyer?.nationalityAr || buyer?.nationalityEn,
              },
            ]
          : []),
        ...(buyerType === BUYER_TYPES?.COMPANY || buyerType === BUYER_TYPES?.GOVERNMENT
          ? [
              {
                label: 'trade_license_number',
                value: buyer?.tradeLicense || buyer?.companyTradeLicenseNumber,
              },
            ]
          : []),
        {
          label: 'Phone_Number',
          value: <bdi>{buyer?.phoneNumber ?? '-'}</bdi>,
        },
        {
          label: 'email',
          value: buyer?.email,
        },
        {
          label: 'buying_percentage',
          value: (
            <div className={classes.transactionShareGridItem}>
              <Typography className={classes.transactionShareTextOne}>
                <span className={classes.transactionShareValue}>{`${buyer?.transactionShare}% `}</span>
                {keyEmptyCheck('out_of', { s: '100%' })}
              </Typography>
              <Typography className={classes.transactionShareTextTwo}>
                {keyEmptyCheck('percentage_of_total_property', { s: buyer?.transactionShare })}
              </Typography>
            </div>
          ),
        },
      ];
    },
    [locale, classes],
  );

  const renderBuyers = () => {
    return buyers?.map((buyer, index) => {
      const buyerType = getBuyerType(buyer);
      if (!buyerType) return null;
      const transformBuyerDetails = transformDetails(buyer, buyerType);
      const buyerDocuments = getBuyerDocuments(buyer, buyerType, documents);
      const buyerRepresentativeDocs = getBuyerRepresentativeDocuments(buyer, buyerType, documents);
      return (
        <div
          key={buyer?.id}
          className={classNames(classes.buyerDetailsHolder, {
            [classes.buyerDetailsHolderWithBorder]: index !== 0,
          })}
        >
          <ADGMGridLayoutV2
            gridData={getBuyerGridLayoutDetails(
              { ...transformBuyerDetails, transactionShare: buyer?.transactionShare },
              buyerType,
            )}
            withIcon
            customClasses={{ container: classes.containerPadding }}
          />
          {!isEmpty(buyerDocuments) && (
            <div className={classNames(classes.documentsHolder, classes.containerPadding)}>
              <DocumentsV2
                title='documents'
                isPanel={false}
                isEditable={false}
                hideDownloadAllBtn
                handleDocDownload={downloadDocumentHandler}
                {...{
                  lang: locale,
                  applicationId: application?.applicationId,
                  applicationType: application?.applicationType,
                  documents: buyerDocuments,
                  attributes: BUYER_DOCUMENTS_ATTRIBUTES[buyerType],
                }}
              />
            </div>
          )}
          <div className={classNames(classes.addRepresentativeHolder)}>
            <AddRepresentative
              isEditable={false}
              application={application}
              party={buyer}
              representative={buyer?.representative}
              isPartyTypeCompany={buyerType === BUYER_TYPES.COMPANY || buyerType === BUYER_TYPES.GOVERNMENT}
              documents={buyerRepresentativeDocs}
              mandatory={buyer?.underage || buyerType === BUYER_TYPES.COMPANY || buyerType === BUYER_TYPES.GOVERNMENT}
              disableAddBtn
              docsAttributes={[
                {
                  active: true,
                  attributeType: 'file',
                  attributeSubType: 'file',
                  name: 'POA_DOC',
                  description: null,
                  title: 'POWER_OF_ATTORNEY',
                  isMultiple: true,
                  mandatory: true,
                  acceptFileTypes: 'application/pdf',
                  ...(buyerType === BUYER_TYPES.UAE_RESIDENT && { userId: buyer?.userId }),
                  ...((buyerType === BUYER_TYPES.COMPANY || buyerType === BUYER_TYPES.GOVERNMENT) && {
                    companyId: buyer?.companyId,
                  }),
                },
              ]}
            />
          </div>
        </div>
      );
    });
  };

  return (
    <ADGMPanel
      title={
        <>
          {keyEmptyCheck('buyer_s_details')}
          <span>{` (${buyers?.length})`}</span>
        </>
      }
    >
      {renderBuyers()}
    </ADGMPanel>
  );
};

BuyersReview.propTypes = {
  classes: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  application: PropTypes.object.isRequired,
  buyers: PropTypes.array.isRequired,
  documents: PropTypes.array.isRequired,
  downloadDocumentHandler: PropTypes.func.isRequired,
};

export default withStyles(styles)(BuyersReview);
